import { useState, useEffect, useRef } from 'react';
import { Input, Select, Spinner } from '@components/ui';
import { FORM_SKIN, ICON_BTN, ICON_BTN_DANGER } from '@components/ui/designTokens';
import { ChevronDownIcon, ChevronUpIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useCountries, useProvinces, useDistricts, useTownships, useNeighborhoods, useLocationCode } from '../hooks/useGeolocation';
import type { Address } from '../types/customer.types';

interface AddressFormProps {
  address?: Partial<Address>;
  onChange: (address: Partial<Address>) => void;
  onRemove?: () => void;
  index: number;
}

export function AddressForm({ address, onChange, onRemove, index }: AddressFormProps) {
  const [formData, setFormData] = useState<Partial<Address>>(
    address || {
      type: 'BILLING',
      is_default: false,
      country: '',
      line1: '',
    }
  );

  const [selectedCountryId, setSelectedCountryId] = useState<string>('');
  const [selectedProvinceId, setSelectedProvinceId] = useState<string>();
  const [selectedDistrictId, setSelectedDistrictId] = useState<string>();
  const [selectedTownshipId, setSelectedTownshipId] = useState<string>();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const isFirstRender = useRef(true);

  // Use ref to always have the latest onChange without triggering re-renders
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Fetch data
  const { data: countries, isLoading: loadingCountries } = useCountries();
  const { data: provinces, isLoading: loadingProvinces } = useProvinces();
  const { data: districts, isLoading: loadingDistricts } = useDistricts(selectedProvinceId);
  const { data: townships, isLoading: loadingTownships } = useTownships(selectedDistrictId);
  const { data: neighborhoods, isLoading: loadingNeighborhoods } = useNeighborhoods(selectedTownshipId);
  const locationCodeMutation = useLocationCode();

  // Set PANAMÁ as default country when countries are loaded and no country is selected
  useEffect(() => {
    if (countries && !formData.country && !selectedCountryId) {
      const PANAMÁ = countries.find(c => c.Name.toUpperCase().includes('PANAM'));
      if (PANAMÁ) {
        const PANAMÁId = PANAMÁ.Id || PANAMÁ.Name;
        setSelectedCountryId(PANAMÁId);
        setFormData(prev => ({
          ...prev,
          country: PANAMÁ.Name,
        }));
      }
    }
  }, [countries, formData.country, selectedCountryId]);

  // Sync selectedCountryId when countries are loaded and we have a country name in formData
  useEffect(() => {
    if (countries && formData.country && !selectedCountryId) {
      const country = countries.find(c => c.Name === formData.country);
      if (country) {
        setSelectedCountryId(country.Id || country.Name);
      }
    }
  }, [countries, formData.country, selectedCountryId]);

  // Sync selectedProvinceId when provinces are loaded and we have a state name in formData
  useEffect(() => {
    if (provinces && formData.state && !selectedProvinceId) {
      const province = provinces.find(p => p.Name === formData.state);
      if (province) {
        setSelectedProvinceId(province.ProvinceId);
      }
    }
  }, [provinces, formData.state, selectedProvinceId]);

  // Sync selectedDistrictId when districts are loaded and we have a district name in formData
  useEffect(() => {
    if (districts && formData.district && !selectedDistrictId && selectedProvinceId) {
      const district = districts.find(d => d.Name === formData.district);
      if (district) {
        setSelectedDistrictId(district.DistrictId);
      }
    }
  }, [districts, formData.district, selectedDistrictId, selectedProvinceId]);

  // Sync selectedTownshipId when townships are loaded and we have a town/township name in formData
  useEffect(() => {
    if (townships && !selectedTownshipId && selectedDistrictId) {
      // Try to find by township field first, then by town field
      const townshipName = formData.township || formData.town;
      if (townshipName) {
        console.log('🔍 Looking for township:', townshipName);
        console.log('📋 Available townships:', townships.map(t => t.Name));
        const township = townships.find(t => t.Name === townshipName);
        if (township) {
          console.log('✅ Found township:', township);
          setSelectedTownshipId(township.TownshipId);
        } else {
          console.warn('⚠️ Township not found:', townshipName);
        }
      }
    }
  }, [townships, formData.town, formData.township, selectedTownshipId, selectedDistrictId]);

  // Check if PANAMÁ is selected (handle both with and without accent)
  const isPANAMÁ = formData.country?.toUpperCase().includes('PANAM');

  // Update parent when formData changes (skip first render to avoid triggering with initial empty state)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    onChangeRef.current(formData);
  }, [formData]);

  // Fetch location code when PANAMÁ address is complete
  useEffect(() => {
    // Only fetch if we don't already have a code and mutation is not already running
    if (isPANAMÁ && formData.state && formData.district && formData.town && !formData.code_address && !locationCodeMutation.isPending) {
      // We already have the names saved in formData, just call the API
      locationCodeMutation.mutate(
        {
          province: formData.state,
          district: formData.district,
          township: formData.town,
        },
        {
          onSuccess: (data) => {
            if (data) {
              setFormData(prev => ({
                ...prev,
                code_address: data.CODIGO_UBICACION,
              }));
            }
          },
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPANAMÁ, formData.state, formData.district, formData.town]);

  const handleFieldChange = (field: keyof Address, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleCountryChange = (value: string) => {
    if (!value) {
      // If empty, reset everything
      setSelectedCountryId('');
      setSelectedProvinceId(undefined);
      setSelectedDistrictId(undefined);
      setSelectedTownshipId(undefined);
      setFormData(prev => ({
        ...prev,
        country: '',
        state: undefined,
        district: undefined,
        town: undefined,
        city: undefined,
        code_address: undefined,
      }));
      return;
    }

    // Try to find country by Id first, then by Name (some APIs use Name as Id)
    let country = countries?.find(c => c.Id === value);
    if (!country) {
      // If not found by Id, try by Name (the value might BE the name)
      country = countries?.find(c => c.Name === value);
    }

    if (country) {
      // Use the actual Id from the country object, or the Name if Id doesn't exist
      const countryId = country.Id || country.Name;

      setSelectedCountryId(countryId);
      setFormData(prev => ({
        ...prev,
        country: country.Name,
        state: undefined,
        district: undefined,
        town: undefined,
        city: undefined,
        code_address: undefined,
      }));
      setSelectedProvinceId(undefined);
      setSelectedDistrictId(undefined);
      setSelectedTownshipId(undefined);
    }
  };

  const handleProvinceChange = (value: string) => {
    // value is the ProvinceId (used for filtering districts)
    setSelectedProvinceId(value);
    const province = provinces?.find(p => p.ProvinceId === value);

    if (province) {
      // Reset dependent fields and SAVE THE NAME (not the ID)
      setSelectedDistrictId(undefined);
      setSelectedTownshipId(undefined);
      setFormData(prev => ({
        ...prev,
        state: province.Name, // Save NAME not ID
        district: undefined,
        town: undefined,
        city: undefined,
        code_address: undefined,
      }));
    }
  };

  const handleDistrictChange = (value: string) => {
    // value is the DistrictId (used for filtering townships)
    setSelectedDistrictId(value);
    const district = districts?.find(d => d.DistrictId === value);

    if (district) {
      // Reset dependent fields and SAVE THE NAME (not the ID)
      setSelectedTownshipId(undefined);
      setFormData(prev => ({
        ...prev,
        district: district.Name, // Save district NAME
        town: undefined, // Reset town, will be set when township is selected
        city: undefined,
        code_address: undefined,
      }));
    }
  };

  const handleTownshipChange = (value: string) => {
    // value is the TownshipId (used for filtering neighborhoods)
    setSelectedTownshipId(value);
    const township = townships?.find(t => t.TownshipId === value);

    if (township) {
      // SAVE THE NAME (not the ID) in both town and township fields
      setFormData(prev => ({
        ...prev,
        town: township.Name, // Save township NAME (corregimiento)
        township: township.Name, // Also save in township field for API compatibility
        city: undefined,
        code_address: undefined,
      }));
    }
  };

  const handleNeighborhoodChange = (value: string) => {
    // value is the NeighborhoodId
    const neighborhood = neighborhoods?.find(n => n.NeighborhoodId === value);

    if (neighborhood) {
      // SAVE THE NAME (not the ID)
      handleFieldChange('city', neighborhood.Name); // Save neighborhood NAME (barrio)
    }
  };

  // Get address summary for collapsed view
  const getAddressSummary = () => {
    if (!formData.country) return 'Sin completar';
    const parts = [formData.line1, formData.city || formData.town, formData.country];
    return parts.filter(Boolean).join(', ') || 'Sin completar';
  };

  return (
    <div className="overflow-hidden rounded-xl border border-[#ececec] bg-white dark:border-secondary-600 dark:bg-secondary-800">
      {/* Header - Always visible */}
      <div className="flex items-center justify-between gap-2 bg-[#fbfbfc] px-4 py-3 dark:bg-secondary-900">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <button
            type="button"
            onClick={() => setIsCollapsed(!isCollapsed)}
            data-testid={`customer-address-form-collapse-button-${index}`}
            aria-expanded={!isCollapsed}
            aria-label={isCollapsed ? 'Expandir dirección' : 'Colapsar dirección'}
            className={ICON_BTN}
          >
            {isCollapsed ? (
              <ChevronDownIcon className="h-4 w-4" />
            ) : (
              <ChevronUpIcon className="h-4 w-4" />
            )}
          </button>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h4 className="text-[14px] font-bold text-ink-850 dark:text-white">
                Dirección {index + 1}
              </h4>
              <span className="rounded-full bg-[#f4f4f4] px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.10em] text-secondary-500 dark:bg-secondary-700 dark:text-secondary-300">
                {formData.type === 'BILLING' ? 'Facturación' : formData.type === 'SHIPPING' ? 'Envío' : 'Otra'}
              </span>
              {formData.is_default && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-teal-500/10 px-2 py-0.5 text-[11px] font-bold uppercase tracking-[0.10em] text-[#0d9488]">
                  <span className="h-1 w-1 rounded-full bg-teal-500" />
                  Predeterminada
                </span>
              )}
            </div>
            {isCollapsed && (
              <p className="mt-0.5 truncate text-xs text-secondary-500 dark:text-secondary-400">
                {getAddressSummary()}
              </p>
            )}
          </div>
        </div>
        {onRemove && (
          <button
            type="button"
            onClick={onRemove}
            data-testid={`customer-address-form-remove-button-${index}`}
            aria-label={`Eliminar dirección ${index + 1}`}
            title="Eliminar"
            className={ICON_BTN_DANGER}
          >
            <TrashIcon className="h-3.5 w-3.5" />
          </button>
        )}
      </div>

      {/* Form content - Collapsible */}
      {!isCollapsed && (
        <div className={`${FORM_SKIN} space-y-3.5 px-4 py-4`}>
          <div className="grid grid-cols-1 gap-3.5 md:grid-cols-2">
        {/* Address Type */}
        <Select
          label="Tipo de Dirección"
          value={formData.type}
          onChange={(e) => handleFieldChange('type', e.target.value)}
          required
          options={[
            { value: 'BILLING', label: 'Facturación' },
            { value: 'SHIPPING', label: 'Envío' },
            { value: 'OTHER', label: 'Otra' }
          ]}
          data-testid={`customer-address-form-type-select-${index}`}
        />

        {/* Default Address */}
        <div className="flex items-end pb-2.5">
          <label className="flex cursor-pointer items-center gap-2.5">
            <input
              type="checkbox"
              checked={formData.is_default}
              onChange={(e) => handleFieldChange('is_default', e.target.checked)}
              data-testid={`customer-address-form-default-checkbox-${index}`}
              className="h-4 w-4 rounded border-secondary-300 text-ink-850 focus:ring-teal-500/20 dark:border-secondary-600"
            />
            <span className="text-[14px] font-medium text-ink-850 dark:text-white">
              Dirección predeterminada
            </span>
          </label>
        </div>

        {/* Country */}
        <Select
          label="País"
          value={selectedCountryId}
          onChange={(e) => handleCountryChange(e.target.value)}
          required
          disabled={loadingCountries}
          data-testid={`customer-address-form-country-select-${index}`}
        >
          <option key="empty" value="">Seleccione un país</option>
          {countries?.map((country) => (
            <option key={country.Id} value={country.Id}>
              {country.Name}
            </option>
          ))}
        </Select>

        {/* PANAMÁ-specific fields */}
        {isPANAMÁ && (
          <>
            {/* Province */}
            <Select
              label="Provincia"
              value={selectedProvinceId || ''}
              onChange={(e) => handleProvinceChange(e.target.value)}
              required
              disabled={loadingProvinces}
              data-testid={`customer-address-form-province-select-${index}`}
            >
              <option key="empty" value="">Seleccione una provincia</option>
              {provinces?.map((province) => (
                <option key={province.ProvinceId} value={province.ProvinceId}>
                  {province.Name}
                </option>
              ))}
            </Select>

            {/* District */}
            <Select
              label="Distrito"
              value={selectedDistrictId || ''}
              onChange={(e) => handleDistrictChange(e.target.value)}
              required
              disabled={!selectedProvinceId || loadingDistricts}
              data-testid={`customer-address-form-district-select-${index}`}
            >
              <option key="empty" value="">Seleccione un distrito</option>
              {districts?.map((district) => (
                <option key={district.DistrictId} value={district.DistrictId}>
                  {district.Name}
                </option>
              ))}
            </Select>

            {/* Township (Corregimiento) */}
            <Select
              label="Corregimiento"
              value={selectedTownshipId || ''}
              onChange={(e) => handleTownshipChange(e.target.value)}
              required
              disabled={!selectedDistrictId || loadingTownships}
              data-testid={`customer-address-form-township-select-${index}`}
            >
              <option key="empty" value="">Seleccione un corregimiento</option>
              {townships?.map((township) => (
                <option key={township.TownshipId} value={township.TownshipId}>
                  {township.Name}
                </option>
              ))}
            </Select>

            {/* Neighborhood (Barrio) and Location Code - Same row */}
            <Select
              label="Barrio"
              value={neighborhoods?.find(n => n.Name === formData.city)?.NeighborhoodId || ''}
              onChange={(e) => handleNeighborhoodChange(e.target.value)}
              disabled={!selectedTownshipId || loadingNeighborhoods}
              data-testid={`customer-address-form-neighborhood-select-${index}`}
            >
              <option key="empty" value="">Seleccione un barrio (opcional)</option>
              {neighborhoods?.map((neighborhood) => (
                <option key={neighborhood.NeighborhoodId} value={neighborhood.NeighborhoodId}>
                  {neighborhood.Name}
                </option>
              ))}
            </Select>

            <Input
              label="Código de Ubicación"
              value={formData.code_address || (locationCodeMutation.isPending ? 'Calculando...' : '')}
              disabled
              readOnly
              placeholder="Se calculará automáticamente"
              data-testid={`customer-address-form-location-code-input-${index}`}
            />
          </>
        )}

        {/* Non-PANAMÁ fields */}
        {!isPANAMÁ && formData.country && (
          <>
            <Input
              label="Ciudad"
              value={formData.city || ''}
              onChange={(e) => handleFieldChange('city', e.target.value)}
              data-testid={`customer-address-form-city-input-${index}`}
              required
            />
            <Input
              label="Código Postal"
              value={formData.postal_code || ''}
              onChange={(e) => handleFieldChange('postal_code', e.target.value)}
              data-testid={`customer-address-form-postal-code-input-${index}`}
            />
          </>
        )}

        {/* Address Lines (for all countries) */}
        {formData.country && (
          <>
            <div className="md:col-span-2">
              <Input
                label="Dirección Línea 1"
                value={formData.line1 || ''}
                onChange={(e) => handleFieldChange('line1', e.target.value)}
                required
                placeholder="Calle, número, edificio..."
                data-testid={`customer-address-form-line1-input-${index}`}
              />
            </div>

            <div className="md:col-span-2">
              <Input
                label="Dirección Línea 2"
                value={formData.line2 || ''}
                onChange={(e) => handleFieldChange('line2', e.target.value)}
                placeholder="Apartamento, piso, referencia..."
                data-testid={`customer-address-form-line2-input-${index}`}
              />
            </div>
          </>
        )}
          </div>

          {/* Loading indicator for location code */}
          {isPANAMÁ && locationCodeMutation.isPending && (
            <div className="flex items-center gap-2 text-sm text-secondary-600 dark:text-secondary-400">
              <Spinner size="sm" />
              <span>Obteniendo código de ubicación...</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
